import type { FC, PropsWithChildren } from "react";
import { useCallback, useMemo } from "react";
import { Button, Card, GeneralDialog, plural, RenderMd } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import type { GetThemeValueForKey } from "tamagui";
import { Dialog, Text, XStack, YStack } from "tamagui";

import { api } from "~/utils/api";
import { OverviewCard, OverviewEmptyCard } from "./overview-common";

export const OverviewScales: FC = () => {
  const { t } = useTranslation("overview");
  const [data] = api.overview.scales.useSuspenseQuery();
  const empty = useMemo(() => data === null, [data]);

  return (
    <OverviewCard title={t("scales.title")} subtitle={t("scales.subtitle")}>
      <Card
        width={"100%"}
        variant="gray"
        backgroundColor={"$warm-snow-500"}
        display={empty ? "none" : "flex"}
      >
        <YStack gap={"$2"}>
          {data?.map((scale) => (
            <ScaleItem
              key={scale.name}
              title={scale.title}
              value={scale.value}
              description={scale.description}
              interpretation={scale.interpretation}
              level={scale.level}
            />
          ))}
        </YStack>
      </Card>
      <OverviewEmptyCard text={t("scales.empty")} visible={empty} />
    </OverviewCard>
  );
};

interface ScaleItemProps {
  title: string;
  value: string | number | null | undefined;
  description: string;
  interpretation: string;
  level: string | undefined;
}

const ScaleItem: FC<ScaleItemProps> = ({
  title,
  value = 0,
  level,
  description,
  interpretation,
}) => {
  const progress = useMemo(() => {
    if (typeof value === "string") {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return value ?? 0;
  }, [value]);

  return (
    <XStack alignItems="center" gap={"$2"} justifyContent="space-between">
      <Text fontFamily={"$body"} lineHeight={"$3"} fontWeight={600} width={180}>
        {title}
      </Text>
      <ScaleProgress value={progress} level={level} />
      <XStack alignItems="center">
        <Text fontFamily={"$body"} fontSize={"$5"} fontWeight={"$5"}>
          {value}
        </Text>
        <Text fontFamily={"$body"} fontSize={"$5"} color={"$gray-600"}>
          /100
        </Text>
      </XStack>
      <ScaleMoreDialog
        title={title}
        description={description}
        interpretation={interpretation}
        value={progress}
        level={level}
      />
    </XStack>
  );
};

const ScaleMoreDialog: FC<
  PropsWithChildren<Omit<ScaleItemProps, "value"> & { value: number }>
> = ({ title, description, interpretation, value, level }) => {
  const { t } = useTranslation("overview");

  const score = useMemo<{
    text: string;
    color: GetThemeValueForKey<"color">;
  }>(() => {
    return {
      color:
        level === "high"
          ? "$error-500"
          : level === "medium"
            ? "$warning-500"
            : "$score-8",
      text: plural(
        value,
        t("scales.scorePlural.one"),
        t("scales.scorePlural.few"),
        t("scales.scorePlural.many"),
      ),
    };
  }, [value, t, level]);

  return (
    <Dialog>
      <Dialog.Trigger asChild>
        <Button
          size="sm"
          variant="outlined"
          color="primary"
          rounded="xl"
          icon={<IconBook />}
          onPress={(e) => e.currentTarget.blur()}
        >
          {t("scales.more")}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <GeneralDialog.Overlay>
          <GeneralDialog.Content width={"100%"} maxWidth={1200}>
            <Card
              gap={"$5"}
              paddingVertical={"$5"}
              paddingHorizontal={"$4"}
              backgroundColor={"#EEEEEE"}
            >
              <Card
                variant="borderless"
                backgroundColor={"white"}
                borderRadius={"$5"}
                gap={"$3"}
                maxHeight={"60vh"}
                style={{ overflowY: "auto" }}
              >
                <GeneralDialog.Title
                  textAlign="left"
                  fontSize={"$7"}
                  lineHeight={"$7"}
                >
                  {title}
                </GeneralDialog.Title>
                <GeneralDialog.Content>
                  <YStack gap={"$2"} marginBottom={"$4"}>
                    <Text
                      fontFamily={"$body"}
                      fontSize={"$5"}
                      fontWeight={"$5"}
                      color={"$gray-600"}
                    >
                      {t("scales.scaleMeaning")}
                    </Text>
                    <RenderMd md={description} />
                  </YStack>
                  <YStack gap={"$2"}>
                    <Text
                      fontFamily={"$body"}
                      fontSize={"$5"}
                      fontWeight={"$5"}
                      color={"$gray-600"}
                    >
                      {t("scales.resultMeaning")}
                    </Text>
                    <Text
                      fontFamily={"$body"}
                      fontSize={"$6"}
                      fontWeight={"$6"}
                      color={score.color}
                    >
                      {`${value} ${score.text}`}
                    </Text>
                    <Text
                      fontFamily={"$body"}
                      fontSize={"$4"}
                      lineHeight={"$4"}
                      fontWeight={"$5"}
                    >
                      {t("scales.scoreDesc")}
                    </Text>
                    <RenderMd md={interpretation} />
                  </YStack>
                </GeneralDialog.Content>
              </Card>
              <Dialog.Close asChild>
                <Button
                  sizes="lg"
                  rounded="lg"
                  variant="primary"
                  alignSelf="center"
                  minWidth={280}
                >
                  {t("scales.close")}
                </Button>
              </Dialog.Close>
            </Card>
          </GeneralDialog.Content>
        </GeneralDialog.Overlay>
      </Dialog.Portal>
    </Dialog>
  );
};

interface ScaleProgressProps {
  value: number | undefined | null;
  level: string | undefined;
  steps?: number;
}
const ScaleProgress: FC<ScaleProgressProps> = ({
  level,
  value = 0,
  steps = 10,
}) => {
  const scale = useMemo(
    () => Array.from({ length: steps }, (_, i) => i + 1),
    [steps],
  );

  const color = useMemo(() => {
    if (!value) return "$gray-100";
    switch (level) {
      case "high":
        return "linear-gradient(90deg, #D6453B 0%, #F0C1BD 100%)";
      case "medium":
        return "linear-gradient(90deg, #F5BA5C 0%, #FFEFD7 100%)";
      default:
        return "linear-gradient(90deg, #309269 0%, #ACD3C3 100%)";
    }
  }, [value, level]);

  const bgByStep = useCallback(
    (step: number): string | undefined => {
      if (!value) return color;
      if (step <= Math.ceil(value / steps)) return color;
    },
    [value, color, steps],
  );

  return (
    <XStack gap={"$0.1"}>
      {scale.map((step) => (
        <Card
          key={step}
          flex={1}
          flexBasis={1}
          height={"$3.4"}
          width={"$6"}
          variant="borderless"
          rounded="none"
          padding={"$0"}
          borderTopLeftRadius={step === 1 ? "$5" : 0}
          borderBottomLeftRadius={step === 1 ? "$5" : 0}
          borderTopRightRadius={step === steps ? "$5" : 0}
          borderBottomRightRadius={step === steps ? "$5" : 0}
          backgroundColor={"$gray-100"}
          style={{ background: bgByStep(step) }}
        />
      ))}
    </XStack>
  );
};

const IconBook: FC = () => {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M10 15.7198C8.31 14.0289 5.7625 13.7181 3.75667 14.7873C3.1875 15.0906 2.5 14.7164 2.5 14.0714V6.49476C2.5 5.96476 2.745 5.45476 3.17667 5.14726C5.2475 3.67059 8.14167 3.86143 10 5.71976C11.8583 3.86143 14.7525 3.67059 16.8233 5.14726C17.255 5.45476 17.5 5.96476 17.5 6.49476V14.0714C17.5 14.7164 16.8125 15.0914 16.2433 14.7873C14.2375 13.7181 11.69 14.0289 10 15.7198Z"
        stroke="#23252A"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 15.7163V5.71631"
        stroke="#23252A"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
